import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Collapse, ListItemIcon, Tooltip } from '@mui/material'
import Link from 'next/link'
import { forwardRef, useMemo, useState } from 'react'
import { SidebarPages } from './SidebarPages'
import {
  SidebarListItemButton,
  SidebarListItemSubButton,
  SidebarListItemSubText,
  SidebarListItemText,
  SidebarSubList,
} from './StyledSidebarComponents'

export type ExpandableMenuItem = {
  type: SidebarPages
  title: string
  href: string
  selected: boolean
}

type ExpandableSidebarLinkProps = {
  isSidebarExpanded: boolean
  selected: boolean
  Icon: React.ElementType
  title: string
  href?: string
  items?: ExpandableMenuItem[]
  onItemClick?: () => void
}

const styleProps = { color: 'fg.muted' }

export const ExpandableSidebarLink = ({
  isSidebarExpanded,
  selected,
  Icon,
  title,
  href,
  items,
  onItemClick,
}: ExpandableSidebarLinkProps) => {
  const [open, setOpen] = useState(selected)
  const hasChildren = items && items.length > 0

  const ListIcon = useMemo(
    () =>
      forwardRef(function ListIconInner(props: any, ref: any) {
        return (
          <ListItemIcon sx={{ minWidth: 24 }} {...props} ref={ref}>
            <Icon sx={{ width: 18, ...styleProps }} />
          </ListItemIcon>
        )
      }),
    [Icon]
  )

  const handleClick = () => {
    if (hasChildren) {
      setOpen(!open)
    }
  }

  const parentButton = (
    <SidebarListItemButton selected={selected} onClick={handleClick}>
      {isSidebarExpanded ? (
        <ListIcon />
      ) : (
        <Tooltip title={title}>
          <ListIcon />
        </Tooltip>
      )}
      {isSidebarExpanded && <SidebarListItemText primary={title} />}
      {isSidebarExpanded && hasChildren && (open ? <ExpandLess sx={styleProps} /> : <ExpandMore sx={styleProps} />)}
    </SidebarListItemButton>
  )

  if (!hasChildren) {
    return parentButton
  }

  // When collapsed, wrap in Link to enable navigation
  if (!isSidebarExpanded && href != null) {
    return (
      <Link href={href} passHref>
        {parentButton}
      </Link>
    )
  }

  return (
    <>
      {parentButton}
      <Collapse in={open && isSidebarExpanded} timeout="auto" unmountOnExit>
        <SidebarSubList>
          {items.map((child) => (
            <Link key={child.type} href={child.href} passHref>
              <SidebarListItemSubButton selected={child.selected} onClick={onItemClick}>
                <SidebarListItemSubText primary={child.title} />
              </SidebarListItemSubButton>
            </Link>
          ))}
        </SidebarSubList>
      </Collapse>
    </>
  )
}
